import {FC} from 'react'
import {Trans, useTranslation} from '@/translations/i18n'
import {Modal, ModalBody, ModalFooter, ModalOverlay, ModalTitle} from '@components/ui/modal-atoms/ModalAtoms'
import {Button} from '@components/ui/button/Button'
import {InputText} from '@components/commons/input-text/InputText'
import {Spinner} from '@components/ui/spinner/Spinner'
import {StyledModalHeader} from '@components/commons/delete-modal/style'
import {UserGroup} from '@/features/user-groups/types'
import {Text} from '@/components/ui/text/Text'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useForm} from 'react-hook-form'

interface DeleteUserGroupModalProps {
    onClose: () => void
    onDelete: () => void
    group: UserGroup
    isDeleting: boolean
}

interface DeleteConfirmationForm {
    confirmation: string
}

export const DeleteUserGroupModal: FC<DeleteUserGroupModalProps> = ({onClose, onDelete, group, isDeleting}) => {
    const {t} = useTranslation()

    const {register, watch} = useForm<DeleteConfirmationForm>({
        defaultValues: {
            confirmation: ''
        }
    })
    const confirmationValue = watch('confirmation')

    return (
        <>
            <ModalOverlay
                onClick={event => {
                    event.stopPropagation()
                    onClose()
                }}
            />
            <Modal width="400px" onClick={event => event.stopPropagation()}>
                <StyledModalHeader>
                    <ModalTitle>{t('user_groups:delete_group')}</ModalTitle>
                    <Flexbox direction="column" gap={2}>
                        <Text size="textMd">
                            <Trans
                                i18nKey="user_groups:delete_group_paragraph_1"
                                values={{name: group.name}}
                                components={[
                                    <Text palette="neutral" shade="950" weight="500" size="textMd" render="span" />
                                ]}
                            />
                        </Text>
                        <Text size="textMd">{t('commons:delete_paragraph_2')}</Text>
                    </Flexbox>
                </StyledModalHeader>
                <ModalBody>
                    <Flexbox direction="column" gap={3}>
                        <Text size="textMd">
                            <Trans
                                i18nKey="user_groups:type_group_name_to_confirm"
                                values={{name: group.name}}
                                components={[
                                    <Text palette="neutral" shade="950" weight="500" size="textMd" render="span" />
                                ]}
                            />
                        </Text>
                        <InputText
                            type="text"
                            placeholder={t('user_groups:type_group_name_placeholder')}
                            {...register('confirmation')}
                        />
                    </Flexbox>
                </ModalBody>
                <ModalFooter>
                    <Button variant="secondary" onClick={onClose}>
                        {t('commons:cancel')}
                    </Button>
                    <Button
                        variant="primaryDanger"
                        disabled={isDeleting || !(confirmationValue === group.name)}
                        onClick={onDelete}
                    >
                        {t('commons:delete')}
                        {isDeleting && <Spinner />}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    )
}
